/**
 * Display-only money helpers. Contract money arrives as decimal strings
 * ("25.00"); it is shown as-is with the EGP code and never recomputed here.
 * No arithmetic, no rounding, no conversion — the server owns all math.
 */

/** True when a nullable limit means "unlimited" rather than zero. */
export function isUnlimited(value: string | number | null | undefined): boolean {
  return value === null || value === undefined || (typeof value === "string" && value.trim() === "");
}

/** Display text for a money string: the string itself + currency, or a fallback. */
export function displayMoney(value: string | null | undefined, currency = "EGP"): string {
  if (value === null || value === undefined || value.trim() === "") return "";
  return `${value} ${currency}`;
}

/** Display text for a count limit: the number, or "" when unlimited. */
export function displayCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

export type RemainingMoneyDisplay =
  | { kind: "unlimited" }
  | { kind: "unknown" }
  | { kind: "value"; value: string };

/**
 * Truthful budget-remaining display. Unlimited ONLY when no budget was
 * configured (null). A configured budget with a missing remaining metric
 * is unknown — never the configured total, never "No limit", never computed.
 */
export function budgetRemainingDisplay(
  configured: string | null | undefined,
  remaining: string | null | undefined,
): RemainingMoneyDisplay {
  if (configured === null || configured === undefined) return { kind: "unlimited" };
  if (remaining === null || remaining === undefined || remaining.trim() === "") return { kind: "unknown" };
  return { kind: "value", value: remaining };
}

export type RemainingCountDisplay =
  | { kind: "unlimited" }
  | { kind: "unknown" }
  | { kind: "value"; value: number };

/** Same rule for use counts: unlimited only when no cap configured. */
export function usesRemainingDisplay(
  configured: number | null | undefined,
  remaining: number | null | undefined,
): RemainingCountDisplay {
  if (configured === null || configured === undefined) return { kind: "unlimited" };
  if (remaining === null || remaining === undefined) return { kind: "unknown" };
  return { kind: "value", value: remaining };
}

/** A configured-only count (no remaining metric exists): null means unlimited. */
export type ConfiguredLimitDisplay = { kind: "unlimited" } | { kind: "value"; value: number };
export function configuredCountDisplay(value: number | null | undefined): ConfiguredLimitDisplay {
  if (value === null || value === undefined) return { kind: "unlimited" };
  return { kind: "value", value };
}
